// carSheets/helpers.js
import { WEEK_KEYS } from "../../earningsImport/assignmentUtils";

export const PLATFORM_ORDER = [
  "UBER",
  "CAREEM",
  "YANGO",
  "BOLT",
  "ZED",
  "KOI",
  "OTHERS",
  "MANUAL",
];

export function makeMonthLabel(monthStr) {
  if (!monthStr) return "";
  const [y, m] = monthStr.split("-").map(Number);
  const date = new Date(y, m - 1, 1);
  return date
    .toLocaleString("en-US", { month: "long", year: "numeric" })
    .toUpperCase();
}

export function makePeriodLabel(from, to) {
  if (!from || !to) return "FULL MONTH";
  const dayFrom = String(from.getDate()).padStart(2, "0");
  const dayTo = String(to.getDate()).padStart(2, "0");
  const monthShort = from
    .toLocaleString("en-US", { month: "short" })
    .toUpperCase();
  return `${dayFrom}–${dayTo} ${monthShort}`;
}

// ----- EXPENSES TEMPLATE (UPDATED AS PER YOUR REQUIREMENT) -----
const BASE_EXPENSE_ROWS = [
  "MONTHLY FIXED COST",
  "SALIK",
  "PARKONIC",
  "RTA PARKING",
  "DARB",
  "PETROL",
  "WPS CHARGES",
  "TRAFFIC FINES",
  "FINE DATE",
  "ADDITIONAL DRIVER",
  "BALANCE BROUGHT FORWARD",
  "ADVANCE PAYMENT",
  "PERMIT RENEWAL",
  "MULKIA/INSURANCE RENEWAL",
  "OTHER EXPENSES",
  "MISC ADJUSTMENT",
  "PARKING, MAINTENANCE ETC",
  "DEWA",
  "TAX",
  "CASH RECEIVED / PAID TO DRIVER",
];

export function createDefaultExpensesTemplate() {
  const rows = BASE_EXPENSE_ROWS.map((label, idx) => {
    const baseRow = {
      id: `exp_${idx}`,
      label,
      col1: "",
      col2: "",
      col3: "",
      total: 0,
    };

    // ⭐ TRAFFIC FINES & FINE DATE → add 10 extra columns
    if (label === "TRAFFIC FINES" || label === "FINE DATE") {
      return {
        ...baseRow,
        col4: "",
        col5: "",
        col6: "",
        col7: "",
        col8: "",
        col9: "",
        col10: "",
      };
    }

    return baseRow;
  });

  // FINAL SUMMARY ROW (UNCHANGED)
  rows.push({
    id: "totalExpenses",
    label: "TOTAL EXPENSES",
    col1: "",
    col2: "",
    col3: "",
    total: 0,
    isSummary: true,
  });

  return rows;
}

// ----- Sheet factory -----
export function createEmptySheet({
  carNumber,
  carModel,
  company,
  month,
  periodStart,
  periodEnd,
}) {
  return {
    id: `${carNumber}-${month}-${periodStart
      .toISOString()
      .slice(0, 10)}-${periodEnd.toISOString().slice(0, 10)}`,
    carNumber,
    carModel,
    company: company || "",
    monthLabel: makeMonthLabel(month),
    periodLabel: makePeriodLabel(periodStart, periodEnd),
    periodStart,
    periodEnd,
    drivers: [],
    expensesRows: createDefaultExpensesTemplate(),
    incomeRows: [],
    cashRows: [],
  };
}

// ------------------------------------------------------------
// NORMALIZERS
// ------------------------------------------------------------
function normalizeText(v) {
  return String(v || "").trim().toUpperCase();
}

function normalizeDriverId(v) {
  return String(v || "").trim();
}

// ------------------------------------------------------------
// INCOME ROW HELPERS
// ------------------------------------------------------------
export function findIncomeRow(incomeRows, company, driver, driverId = "") {
  const companyKey = normalizeText(company);
  const driverKey = normalizeText(driver);
  const driverIdKey = normalizeDriverId(driverId);

  return incomeRows.find((r) => {
    if (r.rowType !== "data") return false;
    if (normalizeText(r.company) !== companyKey) return false;

    const rowDriverId = normalizeDriverId(r.driverId);

    // Prefer strong match by driverId if both sides have it
    if (driverIdKey && rowDriverId) {
      return rowDriverId === driverIdKey;
    }

    // Fallback to driver name
    return normalizeText(r.driver) === driverKey;
  });
}

export function createIncomeRow(incomeRows, company, driver, driverId = "") {
  const safeCompany = String(company || "").replace(/\s+/g, "_");
  const safeDriver = String(driver || "").replace(/\s+/g, "_");
  const safeDriverId = String(driverId || "").replace(/\s+/g, "_");

  const row = {
    id: `inc_${safeCompany}_${safeDriverId || safeDriver}`,
    rowType: "data",
    company,
    driver,
    driverId: driverId || "",
    week1: 0,
    week2: 0,
    week3: 0,
    week4: 0,
    week5: 0,
    total: 0,
  };

  incomeRows.push(row);
  return row;
}

export function addBankIncomeToSheet(
  sheet,
  { platform, driverId = "", driverName, weekKey, amount }
) {
  if (!amount) return;

  const wk = (weekKey || "").toLowerCase();
  if (!WEEK_KEYS.includes(wk)) return;

  let row = findIncomeRow(sheet.incomeRows, platform, driverName, driverId);

  if (!row) {
    row = createIncomeRow(sheet.incomeRows, platform, driverName, driverId);
  } else if (!row.driverId && driverId) {
    // Upgrade older row if it matched by name but had no driverId saved
    row.driverId = driverId;
  }

  row[wk] = (Number(row[wk]) || 0) + Number(amount || 0);
  row.total = (Number(row.total) || 0) + Number(amount || 0);
}

// ------------------------------------------------------------
// CASH ROW HELPERS
// ------------------------------------------------------------
export function findCashRow(cashRows, company, driver, driverId = "") {
  const companyKey = normalizeText(company);
  const driverKey = normalizeText(driver);
  const driverIdKey = normalizeDriverId(driverId);

  return cashRows.find((r) => {
    if (r.rowType !== "data") return false;
    if (normalizeText(r.company) !== companyKey) return false;

    const rowDriverId = normalizeDriverId(r.driverId);

    // Prefer strong match by driverId if both sides have it
    if (driverIdKey && rowDriverId) {
      return rowDriverId === driverIdKey;
    }

    // Fallback to driver name
    return normalizeText(r.driver) === driverKey;
  });
}

export function createCashRow(cashRows, company, driver, driverId = "") {
  const safeCompany = String(company || "").replace(/\s+/g, "_");
  const safeDriver = String(driver || "").replace(/\s+/g, "_");
  const safeDriverId = String(driverId || "").replace(/\s+/g, "_");

  const row = {
    id: `cash_${safeCompany}_${safeDriverId || safeDriver}`,
    rowType: "data",
    company,
    driver,
    driverId: driverId || "",
    week1: 0,
    week2: 0,
    week3: 0,
    week4: 0,
    week5: 0,
    total: 0,
  };

  cashRows.push(row);
  return row;
}

export function addCashTripToSheet(
  sheet,
  { platform, driverId = "", driverName, weekKey, amount }
) {
  if (!amount) return;

  const wk = (weekKey || "").toLowerCase();
  if (!WEEK_KEYS.includes(wk)) return;

  const company = `${platform} CASH`;

  let row = findCashRow(sheet.cashRows, company, driverName, driverId);

  if (!row) {
    row = createCashRow(sheet.cashRows, company, driverName, driverId);
  } else if (!row.driverId && driverId) {
    // Upgrade older row if it matched by name but had no driverId saved
    row.driverId = driverId;
  }

  row[wk] = (Number(row[wk]) || 0) + Number(amount || 0);
  row.total = (Number(row.total) || 0) + Number(amount || 0);
}

// ----- Ensure fixed income rows appear below POS -----
function ensureAdjustmentRowsAfterPos(sheet) {
  const ADJUSTMENT_LABELS = [
    "OTHER ADJUSTMENT",
    "PAYMENT RECEIVED",
  ];

  const UPPER_LABELS = ADJUSTMENT_LABELS.map((l) => l.toUpperCase());

  // Work only with current data rows (no summaries)
  const dataRows = sheet.incomeRows.filter((r) => r.rowType === "data");

  const otherRows = [];
  const existingAdjustments = {};
  let lastPosIndex = -1;

  // 1) Separate normal rows, remember existing adjustment rows, and track last POS row
  dataRows.forEach((r) => {
    const companyUpper = (r.company || "").toUpperCase();
    const driver = r.driver || "";

    // If this is one of our adjustment rows (with empty driver), capture it but don't push yet
    const adjIndex = UPPER_LABELS.indexOf(companyUpper);
    if (adjIndex !== -1 && driver === "") {
      existingAdjustments[companyUpper] = r;
      return;
    }

    // Normal row → keep order
    otherRows.push(r);

    // Track last POS row (company contains 'POS', e.g. 'POS' or 'POS TRANSACTIONS')
    if (companyUpper.includes("POS")) {
      lastPosIndex = otherRows.length - 1;
    }
  });

  // 2) Build the adjustment rows
  const adjustmentRows = ADJUSTMENT_LABELS.map((label, idx) => {
    const keyUpper = UPPER_LABELS[idx];
    const existing = existingAdjustments[keyUpper];

    if (existing) return existing;

    return {
      id: `inc_${label}_`.replace(/\s+/g, "_"),
      rowType: "data",
      company: label,
      driver: "",
      driverId: "",
      week1: 0,
      week2: 0,
      week3: 0,
      week4: 0,
      week5: 0,
      total: 0,
    };
  });

  // 3) Insert adjustment rows after last POS row
  let newIncomeRows;

  if (lastPosIndex >= 0) {
    const beforePos = otherRows.slice(0, lastPosIndex + 1);
    const afterPos = otherRows.slice(lastPosIndex + 1);
    newIncomeRows = [...beforePos, ...adjustmentRows, ...afterPos];
  } else {
    newIncomeRows = [...otherRows, ...adjustmentRows];
  }

  sheet.incomeRows = newIncomeRows;
}

// ----- Finalise totals + summary rows -----
export function finalizeSheetTotals(sheet) {
  console.log(`🔧 FINALIZE SHEET: ${sheet.carNumber} - ${sheet.periodLabel}`);

  // Recalculate row totals from weeks
  sheet.incomeRows.forEach((r) => {
    if (r.rowType === "data") {
      let sum = 0;
      WEEK_KEYS.forEach((wk) => {
        sum += Number(r[wk] || 0);
      });
      r.total = sum;
    }
  });

  sheet.cashRows.forEach((r) => {
    if (r.rowType === "data") {
      let sum = 0;
      WEEK_KEYS.forEach((wk) => {
        sum += Number(r[wk] || 0);
      });
      r.total = sum;
    }
  });

  // TOTAL BANK INCOME OF THE MONTH (Received in Bank) - INCLUDES POS!
  const totalBankIncome = sheet.incomeRows
    .filter((r) => r.rowType === "data")
    .reduce((acc, r) => acc + (Number(r.total) || 0), 0);

  // ⭐⭐ CALCULATE POS TOTAL FROM INCOME ROWS ⭐⭐
  const posTotal = sheet.incomeRows
    .filter((r) => r.rowType === "data" && (r.company || "").toUpperCase().includes("POS"))
    .reduce((acc, r) => acc + (Number(r.total) || 0), 0);

  // TOTAL EXPENSES
  const expenseDataRows = sheet.expensesRows.filter((r) => !r.isSummary);
  const expensesTotal = expenseDataRows.reduce(
    (acc, r) => acc + (Number(r.total) || 0),
    0
  );

  // TOTAL CASH TRIPS
  const totalCashTrips = sheet.cashRows
    .filter((r) => r.rowType === "data")
    .reduce((acc, r) => acc + (Number(r.total) || 0), 0);

  // Remove old summaries
  sheet.incomeRows = sheet.incomeRows.filter((r) => r.rowType !== "summary");

  // Also remove by specific IDs to be sure
  const summaryIds = ["totalIncome", "balance", "cashReceivedPaid", "balancePayable"];
  sheet.incomeRows = sheet.incomeRows.filter((r) => !summaryIds.includes(r.id));

  // Ensure adjustment rows are below POS
  ensureAdjustmentRowsAfterPos(sheet);

  // ⭐⭐ GET CASH RECEIVED/PAID FROM EXPENSES ⭐⭐
  const cashReceivedPaidRow = sheet.expensesRows.find(
    (r) => r.label === "CASH RECEIVED / PAID TO DRIVER"
  );
  const cashReceivedPaid = cashReceivedPaidRow ? Number(cashReceivedPaidRow.total) || 0 : 0;

  // ⭐⭐ CALCULATIONS ⭐⭐
  const balance = totalBankIncome - expensesTotal;
  const balancePayable = balance;

  console.log("📈 SHEET CALCULATIONS:");
  console.log("  Bank Income (incl. POS):", totalBankIncome);
  console.log("  Expenses:", expensesTotal);
  console.log("  Cash Received/Paid (from expenses):", cashReceivedPaid);
  console.log("  Balance (Income - Expenses):", balance);
  console.log("  Balance Payable (Balance - Cash):", balancePayable);
  console.log("  POS Total:", posTotal);
  console.log("  Total Cash Trips:", totalCashTrips);
  console.log("  Balance Cash with Driver:", totalCashTrips - posTotal);

  // 1. TOTAL INCOME OF THE MONTH
  sheet.incomeRows.push({
    id: "totalIncome",
    rowType: "summary",
    label: "TOTAL INCOME OF THE MONTH",
    total: totalBankIncome,
  });

  // 2. BALANCE
  sheet.incomeRows.push({
    id: "balance",
    rowType: "summary",
    label: "BALANCE (Income – Expenses)",
    total: balance,
  });

  // 4. BALANCE PAYABLE / RECEIVABLE
  sheet.incomeRows.push({
    id: "balancePayable",
    rowType: "summary",
    label: "BALANCE PAYABLE / RECEIVABLE",
    total: balancePayable,
  });

  // CASH TRIPS SUMMARY
  sheet.cashRows = sheet.cashRows.filter((r) => r.rowType !== "summary");

  sheet.cashRows.push({
    id: "totalCashTrips",
    rowType: "summary",
    label: "TOTAL CASH TRIPS",
    total: totalCashTrips,
  });

  sheet.cashRows.push({
    id: "lessPos",
    rowType: "summary",
    label: "LESS: POS TRANSACTIONS",
    total: posTotal,
  });

  const balanceCashWithDriver = totalCashTrips - posTotal;
  sheet.cashRows.push({
    id: "balanceCashDriver",
    rowType: "summary",
    label: "BALANCE CASH WITH DRIVER",
    total: balanceCashWithDriver,
  });

  // EXPENSES TOTAL
  const summaryRow = sheet.expensesRows.find((r) => r.isSummary);
  if (summaryRow) {
    summaryRow.total = expensesTotal;
  }

  console.log("✅ FINALIZED VALUES:");
  console.log("  Total Income:", totalBankIncome);
  console.log("  Balance:", balance);
  console.log("  Balance Payable:", balancePayable);
  console.log("  Cash with Driver:", balanceCashWithDriver);
}

// ---------- Manual trips helpers ----------
export function getWeekKeyFromDate(date) {
  const d = date.getDate();
  if (d <= 7) return "week1";
  if (d <= 14) return "week2";
  if (d <= 21) return "week3";
  if (d <= 28) return "week4";
  return "week5";
}

export function getManualTripDriverId(t) {
  return (
    t.mappedDriverId ||
    t.driverId ||
    t.mappingDriverId ||
    t.manualDriverId ||
    null
  );
}

export function getManualTripAmount(t) {
  return (
    Number(t.driverFinalPrice) ||
    Number(t.driverAmount) ||
    Number(t.driverNet) ||
    Number(t.driverPrice) ||
    0
  );
}

export function getManualTripDate(t) {
  const raw =
    t.tripDate ||
    t.serviceDate ||
    t.date ||
    t.pickupTime ||
    t.batchDate;

  if (!raw) return null;

  return raw.seconds ? new Date(raw.seconds * 1000) : new Date(raw);
}